/**
 * FuelGauge — Reusable fuel status bar
 * Compact mode: [████████░░] 72% 1.7h  (for table cells)
 * Full mode: Vertical bars with threshold markers
 */

const STATUS_COLORS: Record<string, string> = {
  GREEN: '#22c55e',
  AMBER: '#f59e0b',
  RED: '#ef4444',
  BLACK: '#6b7280',
}

function getStatusColor(pct: number): string {
  if (pct >= 0.60) return STATUS_COLORS.GREEN
  if (pct >= 0.30) return STATUS_COLORS.AMBER
  if (pct >= 0.15) return STATUS_COLORS.RED
  return STATUS_COLORS.BLACK
}

interface FuelGaugeProps {
  fuelPct: number       // 0-1
  status: string        // GREEN / AMBER / RED / BLACK
  timeToBingo?: number  // hours
  compact?: boolean
}

export function FuelGauge({ fuelPct, status, timeToBingo, compact = false }: FuelGaugeProps) {
  const filled = Math.round(fuelPct * 10)
  const color = getStatusColor(fuelPct)
  const pctDisplay = Math.round(fuelPct * 100)

  if (compact) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex gap-[1px]">
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className="w-[5px] h-3 rounded-[1px]"
              style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }}
            />
          ))}
        </div>
        <span className="text-[10px] tabular-nums w-7 text-right" style={{ color }}>
          {pctDisplay}%
        </span>
        {timeToBingo !== undefined && (
          <span className="text-[9px] tabular-nums text-term-textDim">
            {timeToBingo.toFixed(1)}h
          </span>
        )}
      </div>
    )
  }

  // Full mode
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold tabular-nums" style={{ color }}>
          {pctDisplay}%
        </span>
        <span
          className="text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: `${color}20`, color, border: `1px solid ${color}40` }}
        >
          {status}
        </span>
      </div>
      <div className="flex gap-[2px] h-4">
        {Array.from({ length: 10 }, (_, i) => (
          <div
            key={i}
            className="flex-1 rounded-sm relative"
            style={{ backgroundColor: i < filled ? color : '#3a3a5c40' }}
          >
            {/* Threshold markers at 60%, 30%, 15% */}
            {i === 5 && <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-green-500/40" />}
            {i === 2 && <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-amber-500/40" />}
            {i === 1 && <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-red-500/40" />}
          </div>
        ))}
      </div>
      {timeToBingo !== undefined && (
        <div className="text-[10px] text-term-textDim">
          Time to bingo: <span className="font-bold text-term-text">{timeToBingo.toFixed(1)}h</span>
        </div>
      )}
    </div>
  )
}
